import { useMemo, useRef } from 'react';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useManagePicTransactionsQuery from './hooks.useManagePicTransactionsQuery';
import useRows from './hooks.useRows';
import { PH_PROGRAM_NAME } from '../GeneralAffordable/constants';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useHeaders from './hooks.useHeaders';

import useModalProps from '../../hooks/useModalProps';
import useCheckboxSelection from '../../hooks/useCheckboxSelection';
import usePicTransactionsCSVExport from './hooks.usePicTransactionsCSVExport';
import useParseResults from './hooks.useParseResults';

import { PROPERTY_PATH_MAP } from './constants';

const useManagePicTransactions = ({
  organizationId,
  propertyId,
  propertyName,
}) => {
  const name = useUniqueTableName('manage-pic-transactions-table');
  const selectedProperty = useSelectedProperty();

  const certificationTypeProgramName = useMemo(() => {
    const hasPh = selectedProperty?.pap?.some(
      (pap) => pap.masterAffordableProgram?.name === PH_PROGRAM_NAME,
    );
    return hasPh ? PH_PROGRAM_NAME : null;
  }, [selectedProperty]);

  const {
    data,
    isLoading,
    refetch: refetchTransactions,
  } = useManagePicTransactionsQuery({
    organizationId,
    propertyId,
    startDate: null,
    endDate: null,
  });

  const parsedResults = useParseResults({
    results: data,
    certificationTypeProgramName,
  });

  const visibleRowsForSelectAllRef = useRef([]);

  const {
    selectedIds,
    handleSelectRow,
    handleSelectDeSelectAllRows,
    isAtLeastOneRowSelected,
  } = useCheckboxSelection({
    results: parsedResults,
    getRowsForSelectAll: () => visibleRowsForSelectAllRef.current,
  });

  const headers = useHeaders({
    results: data,
    certificationTypeProgramName,
    checkBoxListProps: {
      handleSelectDeSelectAllRows,
    },
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    initialOrderBy: 'certificationCompletionDate',
    initialOrder: 'DESC',
    headers,
    initialFilterState: { status: [''] },
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: parsedResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  visibleRowsForSelectAllRef.current = sortedAndFilteredResults;

  const selectedAffordableQualificationIds = useMemo(
    () =>
      parsedResults
        .filter((r) => selectedIds.has(r.id))
        .map((r) => r.affordableQualificationId)
        .filter(Boolean),
    [parsedResults, selectedIds],
  );

  const modalProps = useModalProps();

  const rows = useRows({
    picTransactions: sortedAndFilteredResults,
    checkBoxListProps: {
      selectedIds,
      handleSelectRow,
    },
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  const { onCSVButtonClick } = usePicTransactionsCSVExport({
    filteredHeaders,
    rows,
    hasAnyFilters: Boolean(Object.keys(filterState)?.length),
  });

  return {
    modalProps,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleColumnChange,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    isLoading,
    count: rows.length ?? 0,
    totalCount: sortedAndFilteredResults.length ?? 0,
    name,
    order,
    orderBy,
    filteredHeaders,
    refetchTransactions,
    rows,
    onCSVButtonClick,
    selectedIds,
    isAtLeastOneRowSelected,
    selectedAffordableQualificationIds,
  };
};

export default useManagePicTransactions;
